import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { HostQuotasTab } from '@/components/guests/HostQuotasTab';
import { celebrationService } from '@/lib/api/celebration.service';
import { eventService } from '@/lib/api/event.service';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { handleApiError } from '@/lib/api/client';
import { useAuth } from '@/hooks/useAuth';
import type { Event } from '@/types/event.types';

interface CelebrationQuotasSectionProps {
  celebrationId: number;
}

export function CelebrationQuotasSection({ celebrationId }: CelebrationQuotasSectionProps) {
  const { user } = useAuth();
  const [data, setData] = useState<{ events: Event[]; canEdit: boolean }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      celebrationService.getCelebration(celebrationId),
      eventService.getEvents(),
      teamMemberService.getTeamMembers(celebrationId),
    ]).then(([celebration, events, members]) => {
      if (cancelled) return;
      setData({
        events: events.filter(event => event.celebration_id === celebration.id && !event.deleted_at),
        canEdit: members.some(member => member.user_id === user?.id && member.status === 'active' && member.role === 'organizer'),
      });
    }).catch((failure: unknown) => {
      if (!cancelled) setError(handleApiError(failure) || 'Failed to load celebration quotas');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [celebrationId, user?.id, attempt]);

  return (
    <section id="guest-quotas" className="scroll-mt-24 space-y-3">
      <header className="rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-4">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <ShieldAlert className="h-5 w-5 text-cyan-600" />
          Guest Quotas &amp; Allocations
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">Review host quotas and event allocations. Only organizers can change allocations.</p>
      </header>

      {loading ? (
        <Card><CardContent className="flex items-center gap-2 p-5 text-sm text-muted-foreground"><RefreshCw className="h-4 w-4 animate-spin" />Loading quotas...</CardContent></Card>
      ) : error || !data ? (
        <Card><CardContent className="flex items-center justify-between gap-3 p-5 text-sm text-red-700"><span>{error || 'Failed to load celebration quotas'}</span><button type="button" onClick={retry} className="font-medium underline">Retry</button></CardContent></Card>
      ) : (
        <HostQuotasTab celebrationId={celebrationId} events={data.events} canEdit={data.canEdit} />
      )}
    </section>
  );
}